import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import NudgeSheet from '@/components/sheets/NudgeSheet.jsx';
import { DeclineDialog } from '@/components/account/MarketLadderPanel.jsx';
import { listFollowUpsDue } from '@/services/markets.service.js';
import { api } from '@/lib/api.js';
import { formatDateOrEmpty } from '@/lib/date-display.js';

// MV-02 2.3 (Ship 2 item 3): "Submissions needing follow-up" — every market
// row across deals whose quiet time passed its carrier's nudge threshold.
// Due-ness is DERIVED server-side at read time (no stored queue rows; the
// Ship 4 what's-waiting queue consumes the same derivation). One click opens
// the follow-up draft; the broker sends.

const STATE_LABEL = {
  sent: 'Sent',
  acknowledged: 'Acknowledged',
  working: 'Working',
  no_response: 'No response',
};

export default function FollowUps() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  // ?nudge=<marketId> deep link (Ship 4a): the queue's "Draft follow-up"
  // action lands with the NudgeSheet already open on the exact market.
  const [searchParams, setSearchParams] = useSearchParams();
  const [nudgeMarketId, setNudgeMarketId] = useState(() => {
    const fromLink = Number(searchParams.get('nudge'));
    return Number.isFinite(fromLink) && fromLink > 0 ? fromLink : null;
  });
  // MV-03 §2.3: quote OFFERS lapsing unactioned — derived server-side
  // (quote_expires_at + admin-config lead), no stored queue rows.
  const [expiring, setExpiring] = useState([]);
  // The quiet row's OTHER honest answer (187 addendum 2 §4). Silence can mean
  // "they haven't got to it" or "they are not offering terms", and only David
  // can read which — so the follow-up offers both verbs and presumes neither.
  const [declineMarket, setDeclineMarket] = useState(null);

  const refresh = useCallback(() => {
    listFollowUpsDue()
      .then((d) => { setRows(d.follow_ups || []); setError(''); })
      .catch((err) => setError(err.body?.error || err.message || 'Load failed'));
    api.get(`/api/tracon/quotes/expiring?_t=${Date.now()}`)
      .then((d) => setExpiring(d.quotes || []))
      .catch(() => setExpiring([]));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold text-text">Submissions needing follow-up</h2>
      <p className="mt-1 text-sm text-text-muted">
        Markets quiet past their follow-up threshold. Thresholds are per carrier in Admin, Carriers.
      </p>

      {error ? (
        <p className="mt-6 text-sm text-danger">{error}</p>
      ) : rows === null ? (
        <p className="mt-6 text-sm text-text-muted">Checking the ladder…</p>
      ) : rows.length === 0 ? (
        <div className="mt-6 rounded-card border border-dashed border-border p-6 text-sm text-text-muted">
          Nothing waiting. Every dispatched market has been touched inside its follow-up window.
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card border border-border bg-surface px-3.5 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-text">
                  <Link to={`/account/${r.crm_deal_id}/deal/${r.crm_deal_id}`} className="hover:underline">
                    {r.named_insured || r.crm_deal_id}
                  </Link>
                  {r.tails ? <span className="ml-2 text-xs text-text-muted">{r.tails}</span> : null}
                </p>
                <p className="text-[11px] text-text-muted">
                  {r.carrier_name || (r.recipients || []).map((x) => x.email).join(', ') || 'Ad-hoc'}
                  {' · sent '}{formatDateOrEmpty(r.sent_at)}
                  {r.clearing ? ' · clearing send' : ''}
                </p>
              </div>
              <Badge variant="outline">{STATE_LABEL[r.state] || r.state}</Badge>
              <Badge variant="warning">{r.nudge.days_quiet}d quiet</Badge>
              <Button variant="outline" size="sm" onClick={() => setNudgeMarketId(r.id)}>
                <Mail size={14} strokeWidth={1.5} /> Draft follow-up
              </Button>
            </div>
          ))}
        </div>
      )}

      {expiring.length > 0 && (
        <div className="mt-8">
          <h3 className="text-base font-semibold text-text">Quote offers expiring unactioned</h3>
          <p className="mt-0.5 text-xs text-text-muted">
            The carrier gave these terms a lapse date and nobody has selected or declined yet.
          </p>
          <div className="mt-3 space-y-2">
            {expiring.map((q) => (
              <div key={q.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card border border-border bg-surface px-3.5 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-text">
                    <Link to={`/account/${q.deal_id}/deal/${q.deal_id}`} className="hover:underline">
                      {q.deal_name || q.deal_id}
                    </Link>
                    {q.tail ? <span className="ml-2 text-xs text-text-muted">{q.tail}</span> : null}
                  </p>
                  <p className="text-[11px] text-text-muted">
                    {q.carrier}
                    {q.quote_number ? ` · #${q.quote_number}` : ''}
                    {q.round > 1 ? ` · round ${q.round}` : ''}
                    {q.status === 'draft' ? ' · still a parse draft' : ''}
                  </p>
                </div>
                <Badge variant="warning">
                  {q.expired ? 'offer expired' : q.days_left === 0 ? 'expires today' : `expires in ${q.days_left}d`}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      <NudgeSheet
        marketId={nudgeMarketId}
        open={nudgeMarketId != null}
        onOpenChange={(o) => {
          if (!o) {
            setNudgeMarketId(null);
            if (searchParams.has('nudge')) setSearchParams({}, { replace: true });
          }
        }}
        onSent={refresh}
        onRecordDecline={(id) => {
          const row = (rows || []).find((r) => String(r.id) === String(id));
          setDeclineMarket(row || { id });
        }}
      />

      <DeclineDialog
        market={declineMarket || { id: null }}
        open={declineMarket != null}
        onOpenChange={(o) => { if (!o) setDeclineMarket(null); }}
        onChanged={refresh}
      />
    </div>
  );
}
